<script setup lang="ts">
/*
 * Lightweight date input — styled native <input type="date">. Matches the
 * other form inputs visually. The prototype's V6.0 .dob-popup (year/month
 * pickers + inline calendar) lands as a separate, optional enhancement
 * during/after Phase 0.7, since most run/payroll screens use month-pickers
 * (MonthPicker — to be added in 0.4d) rather than full DOB selection.
 */
import { computed, inject, type Ref, type ComputedRef } from 'vue';

const props = withDefaults(
  defineProps<{
    modelValue?: string | null;
    min?: string;
    max?: string;
    disabled?: boolean;
    readonly?: boolean;
    invalid?: boolean;
    size?: 'sm' | 'md';
  }>(),
  { size: 'md' },
);

defineEmits<{ 'update:modelValue': [v: string] }>();

const field = inject<{
  inputId: Ref<string>;
  describedBy: ComputedRef<string>;
  invalid: ComputedRef<boolean>;
}>('formField', undefined as never);

const isInvalid = computed(() => props.invalid || field?.invalid.value || false);
</script>

<template>
  <input
    :id="field?.inputId.value"
    type="date"
    :value="modelValue ?? ''"
    :min="min"
    :max="max"
    :disabled="disabled"
    :readonly="readonly"
    :aria-invalid="isInvalid || undefined"
    :aria-describedby="field?.describedBy.value || undefined"
    :class="[
      'w-full border bg-app-surface text-app-fg transition-shadow',
      'focus:outline-none focus:ring-3 focus:ring-primary/20 focus:border-primary',
      'disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500',
      // sm gets a tighter radius + smaller text to match the reduced height.
      size === 'sm' ? 'rounded-md px-3 py-1.5 text-sm' : 'rounded-xl px-3.5 py-2.5 text-base',
      isInvalid ? 'border-red-300' : 'border-app-border',
    ]"
    @input="$emit('update:modelValue', ($event.target as HTMLInputElement).value)"
  />
</template>
